import { useState, useCallback, useRef } from 'react';

/**
 * AlertModal을 쉽게 사용하기 위한 커스텀 훅
 * alert()와 confirm()을 대체하는 통합 모달 관리
 *
 * 큐(Queue) 시스템으로 여러 모달 요청을 순차적으로 처리
 *
 * @returns {Object} 모달 상태 및 제어 함수들
 */
export const useAlertModal = () => {
  // Alert 모달 큐
  const [alertQueue, setAlertQueue] = useState([]);
  // [{ id, message, type, callback }, ...]

  // Confirm 모달 큐
  const [confirmQueue, setConfirmQueue] = useState([]);
  // [{ id, message, callback }, ...]

  // 고유 ID 생성용
  const idCounterRef = useRef(0);

  const generateId = useCallback(() => {
    idCounterRef.current += 1;
    return idCounterRef.current;
  }, []);

  // 현재 표시할 Alert (큐의 첫 번째 항목)
  const currentAlert = alertQueue.length > 0 ? alertQueue[0] : null;
  const showAlert = currentAlert !== null;
  const alertMessage = currentAlert?.message || '';
  const alertType = currentAlert?.type || 'info';

  // 현재 표시할 Confirm (큐의 첫 번째 항목)
  const currentConfirm = confirmQueue.length > 0 ? confirmQueue[0] : null;
  const showConfirm = currentConfirm !== null;
  const confirmMessage = currentConfirm?.message || '';

  /**
   * Alert 모달 표시 (일반 버전 - 큐에 추가)
   * @param {string} message - 표시할 메시지
   * @param {string} type - 메시지 타입 ('success', 'warning', 'error', 'info')
   */
  const showAlertModal = useCallback((message, type = 'info') => {
    const id = generateId();
    setAlertQueue(prev => [...prev, { id, message, type, callback: null }]);
  }, [generateId]);

  /**
   * Alert 모달 표시 (Promise 기반 - 사용자가 확인 버튼 클릭할 때까지 대기)
   * @param {string} message - 표시할 메시지
   * @param {string} type - 메시지 타입 ('success', 'warning', 'error', 'info')
   * @returns {Promise<void>} 사용자가 확인 버튼을 클릭하면 resolve
   *
   * @example
   * await showAlertModalAsync('저장되었습니다!', 'success');
   * // 사용자가 확인 버튼을 클릭한 후 다음 코드 실행
   * console.log('사용자가 확인함');
   */
  const showAlertModalAsync = useCallback((message, type = 'info') => {
    return new Promise((resolve) => {
      const id = generateId();
      setAlertQueue(prev => [...prev, { id, message, type, callback: resolve }]);
    });
  }, [generateId]);

  /**
   * Confirm 모달 표시 (Promise 기반 - 큐에 추가)
   * @param {string} message - 표시할 메시지
   * @returns {Promise<boolean>} 사용자 선택 결과 (확인: true, 취소: false)
   *
   * @example
   * const confirmed = await showConfirmModal('정말 삭제하시겠습니까?');
   * if (confirmed) {
   *   // 삭제 로직
   * }
   */
  const showConfirmModal = useCallback((message) => {
    return new Promise((resolve) => {
      const id = generateId();
      setConfirmQueue(prev => [...prev, { id, message, callback: resolve }]);
    });
  }, [generateId]);

  /**
   * Confirm 모달에서 확인 클릭
   */
  const handleConfirm = useCallback(() => {
    if (currentConfirm && currentConfirm.callback) {
      currentConfirm.callback(true); // Promise resolve with true
    }
    // 큐에서 현재 항목 제거
    setConfirmQueue(prev => prev.slice(1));
  }, [currentConfirm]);

  /**
   * Confirm 모달에서 취소 클릭
   */
  const handleCancel = useCallback(() => {
    if (currentConfirm && currentConfirm.callback) {
      currentConfirm.callback(false); // Promise resolve with false
    }
    // 큐에서 현재 항목 제거
    setConfirmQueue(prev => prev.slice(1));
  }, [currentConfirm]);

  /**
   * Alert 모달 닫기
   */
  const closeAlert = useCallback(() => {
    if (currentAlert && currentAlert.callback) {
      currentAlert.callback(); // Promise resolve
    }
    // 큐에서 현재 항목 제거
    setAlertQueue(prev => prev.slice(1));
  }, [currentAlert]);

  return {
    // Alert 상태
    showAlert,
    alertMessage,
    alertType,
    closeAlert,

    // Confirm 상태
    showConfirm,
    confirmMessage,
    handleConfirm,
    handleCancel,

    // 함수들
    showAlertModal,
    showAlertModalAsync,
    showConfirmModal
  };
};
